import { React, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Form from "react-bootstrap/Form";
import { Spinner } from "react-bootstrap";
import ApiConfig from "../../api/ApiConfig";
import {
  getWithAuthCall,
  simplePUTCall,
  simplePostCall,
  postMultipartWithAuthCallWithErrorResponse,
} from "../../api/ApiServices";
import { notifySuccess, notifyError } from "../../sharedComponent/notify";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

const FinanceSettings = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [form, setForm] = useState({
    currency_code: "",
    corporate_tax_rate: "",
    tax_depreciation_rate: "",
    default_expected_km: "",
    default_planned_cpk: "",
  });
  const [currencyName, setCurrencyName] = useState("");

  // Per-vehicle-type planned CPK
  const [typeCpk, setTypeCpk] = useState([]);
  const [typeCpkLoading, setTypeCpkLoading] = useState(true);
  const [savingTypeId, setSavingTypeId] = useState(null);

  useEffect(() => {
    getProfileCurrency();
    getSettings();
    getTypeCpk();
  }, []);

  // Currency is driven by the Customer Profile (Master Settings), not entered here
  const getProfileCurrency = () => {
    postMultipartWithAuthCallWithErrorResponse(ApiConfig.GET_CUSTOMER_PROFILE)
      .then((res) => {
        const d = res?.json?.data;
        if (res?.json?.result && d) {
          setForm((prev) => ({
            ...prev,
            currency_code: d.currency_code || prev.currency_code,
          }));
          setCurrencyName(
            [d.currency_name, d.currency_symbol && `(${d.currency_symbol})`]
              .filter(Boolean)
              .join(" ")
          );
        }
      })
      .catch((err) => console.log("profile currency err", err));
  };

  const getSettings = () => {
    setLoading(true);
    getWithAuthCall(ApiConfig.FLEET_FINANCE_SETTINGS)
      .then((data) => {
        const d = data?.data || data?.settings || {};
        if (data?.success || data?.result || d) {
          setForm((prev) => ({
            ...prev,
            // currency_code is sourced from the Customer Profile, not finance settings
            corporate_tax_rate: d.corporate_tax_rate ?? "",
            tax_depreciation_rate: d.tax_depreciation_rate ?? "",
            default_expected_km: d.default_expected_km ?? "",
            default_planned_cpk: d.default_planned_cpk ?? "",
          }));
        }
      })
      .catch((err) => console.log("settings err", err))
      .finally(() => setLoading(false));
  };

  const getTypeCpk = () => {
    setTypeCpkLoading(true);
    getWithAuthCall(ApiConfig.FLEET_FINANCE_VEHICLE_TYPE_CPK_LIST)
      .then((data) => {
        const d = data?.data || data?.result;
        setTypeCpk(Array.isArray(d) ? d : Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.log("vehicle-type-cpk list err", err);
        setTypeCpk([]);
      })
      .finally(() => setTypeCpkLoading(false));
  };

  const changeTypeCpk = (typeId, value) => {
    setTypeCpk((prev) =>
      prev.map((r) =>
        r.vehicle_type_id === typeId ? { ...r, planned_cpk: value } : r
      )
    );
  };

  const saveTypeCpk = (row) => {
    setSavingTypeId(row.vehicle_type_id);
    const body = JSON.stringify({
      vehicle_type_id: row.vehicle_type_id,
      planned_cpk: Number(row.planned_cpk) || 0,
    });
    simplePUTCall(ApiConfig.FLEET_FINANCE_VEHICLE_TYPE_CPK_UPDATE, body)
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Planned CPK updated");
        } else {
          notifyError(data?.message || "Unable to update planned CPK");
        }
      })
      .catch((err) => {
        console.log("vehicle-type-cpk update err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setSavingTypeId(null));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSeed = () => {
    setSeeding(true);
    simplePostCall(ApiConfig.FLEET_FINANCE_SEED, JSON.stringify({}))
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Chart of accounts seeded");
        } else {
          notifyError(data?.message || "Unable to seed");
        }
      })
      .catch((err) => {
        console.log("seed err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setSeeding(false));
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);
    const body = JSON.stringify({
      currency_code: form.currency_code,
      corporate_tax_rate: Number(form.corporate_tax_rate) || 0,
      tax_depreciation_rate: Number(form.tax_depreciation_rate) || 0,
      default_expected_km: Number(form.default_expected_km) || 0,
      default_planned_cpk: Number(form.default_planned_cpk) || 0,
    });
    simplePUTCall(ApiConfig.FLEET_FINANCE_SETTINGS_UPDATE, body)
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Settings updated");
          getSettings();
        } else {
          notifyError(data?.message || "Unable to update settings");
        }
      })
      .catch((err) => {
        console.log("update settings err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setSaving(false));
  };

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_Settings">
        <div className="main-master-wrapper">
          <div
            className="d-flex justify-content-between align-items-center flex-wrap"
            style={{
              borderBottom: "0.5px solid #f6efe9",
              paddingBottom: 12,
              marginBottom: 20,
              gap: 12,
            }}
          >
            <div className="vehicleHead">
              <p style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
                {t("Finance Settings")}
              </p>
            </div>
            <button
              className="cx-btn-2"
              onClick={handleSeed}
              disabled={seeding}
            >
              {seeding ? (
                <Spinner animation="border" size="sm" />
              ) : (
                t("Seed Chart of Accounts")
              )}
            </button>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <Form onSubmit={handleSave}>
              <div className="innerInputsGen mainVehAccident">
                <div className="addVehicleSe">
                  <div className="addVehicleLeftSec">
                    <div className="row">
                      <div className="col-md-4 form_input_main">
                        <div className="innerSelectBox weekCounter">
                          <Form.Label className="common-labels">
                            {t("Currency")}{" "}
                            <small style={{ opacity: 0.6 }}>
                              ({t("from Customer Profile")})
                            </small>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="currency_code"
                            value={
                              currencyName
                                ? `${form.currency_code} ${currencyName}`
                                : form.currency_code
                            }
                            readOnly
                            disabled
                          />
                        </div>
                      </div>
                      <div className="col-md-4 form_input_main">
                        <div className="innerSelectBox weekCounter">
                          <Form.Label className="common-labels">
                            {t("Corporate Tax Rate")} (%)
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="corporate_tax_rate"
                            value={form.corporate_tax_rate}
                            onChange={handleChange}
                            placeholder="10"
                          />
                        </div>
                      </div>
                      <div className="col-md-4 form_input_main">
                        <div className="innerSelectBox weekCounter">
                          <Form.Label className="common-labels">
                            {t("Tax Depreciation Rate")} (%)
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="tax_depreciation_rate"
                            value={form.tax_depreciation_rate}
                            onChange={handleChange}
                            placeholder="20"
                          />
                        </div>
                      </div>
                      <div className="col-md-4 form_input_main">
                        <div className="innerSelectBox weekCounter">
                          <Form.Label className="common-labels">
                            {t("Default Expected KM")}{" "}
                            <small style={{ opacity: 0.6 }}>
                              ({t("annual, utilization fallback")})
                            </small>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="default_expected_km"
                            value={form.default_expected_km}
                            onChange={handleChange}
                            placeholder="100000"
                          />
                        </div>
                      </div>
                      <div className="col-md-4 form_input_main">
                        <div className="innerSelectBox weekCounter">
                          <Form.Label className="common-labels">
                            {t("Default Planned CPK")}{" "}
                            <small style={{ opacity: 0.6 }}>
                              ({t("CPK variance fallback")})
                            </small>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            name="default_planned_cpk"
                            value={form.default_planned_cpk}
                            onChange={handleChange}
                            placeholder="1.0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex" style={{ gap: 12, marginTop: 16 }}>
                <button className="cx-btn-2" type="submit" disabled={saving}>
                  {saving ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    t("Save Settings")
                  )}
                </button>
              </div>
            </Form>
          )}

          {/* ===== Per-vehicle-type planned CPK ===== */}
          <div
            className="vehicleHead"
            style={{ marginTop: 32, marginBottom: 12 }}
          >
            <p style={{ marginBottom: 0 }}>{t("Vehicle Type Planned CPK")}</p>
          </div>
          {typeCpkLoading ? (
            <Loader />
          ) : (
            <div className="VA_table">
              <table className="table tableAdmin">
                <thead className="tableHead">
                  <tr>
                    <th>{t("Vehicle Type")}</th>
                    <th>{t("Vehicles")}</th>
                    <th>{t("Planned CPK")}</th>
                    <th>{t("Action")}</th>
                  </tr>
                </thead>
                <tbody className="tableBody">
                  {typeCpk && typeCpk.length > 0 ? (
                    typeCpk.map((r) => (
                      <tr key={r.vehicle_type_id}>
                        <td>{r.type || r.vehicle_type || r.vehicle_type_id}</td>
                        <td>{r.vehicle_count ?? "-"}</td>
                        <td style={{ maxWidth: 160 }}>
                          <Form.Control
                            type="number"
                            step="0.01"
                            value={r.planned_cpk ?? ""}
                            onChange={(e) =>
                              changeTypeCpk(r.vehicle_type_id, e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="cx-btn-1"
                            onClick={() => saveTypeCpk(r)}
                            disabled={savingTypeId === r.vehicle_type_id}
                          >
                            {savingTypeId === r.vehicle_type_id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              t("Save")
                            )}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4}>
                        <NoDataComp />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default FinanceSettings;
